import { AdminRole } from './user.types';

export interface PayoutHybridSourceFields {
  hybridEligibleCount?: number;
  hybridThreshold?: number;
  hybridQualifyingDeliveryIds?: number[];
  hybridDeliveryBasis?: 'STANDARD' | 'FIXED_PER_DELIVERY';
  hybridQualifies?: boolean;
}

export type PayoutAssignmentTarget = 'RIDER' | 'COHORT' | 'PARTNER';
export type PayoutDeliveryEarningMethod =
  | 'STANDARD'
  | 'FIXED_PER_DELIVERY'
  | 'DAILY'
  | 'HYBRID';
export type PayoutRecipientMode = 'RIDER' | 'PARTNER';

export interface CommercialFeeRule {
  name: string;
  type: 'FIXED' | 'PERCENTAGE';
  basis: 'DELIVERY_EARNINGS' | 'DAILY_EARNINGS';
  frequency: 'PER_DELIVERY' | 'PER_ELIGIBLE_DAY' | 'PER_PERIOD';
  value: string;
  includeTips: boolean;
  includeBonuses: boolean;
}

export interface ConfigurePayoutAgreementInput {
  agreementId?: string;
  name?: string;
  revision: {
    recipientMode: PayoutRecipientMode;
    earningMethod?: PayoutDeliveryEarningMethod;
    fixedDeliveryAmount?: string | null;
    hybridRule?: 'DAILY_PLUS_DELIVERY' | null;
    hybridDeliveryBasis?: 'STANDARD' | 'FIXED_PER_DELIVERY' | null;
    hybridThreshold?: number | null;
    hybridCountScope?: 'SERVICE_DAY' | null;
    hybridEligibilityBasis?: 'COMPLETED_DELIVERIES_ON_APPROVED_DAY' | null;
    dailyAmount?: string | null;
    dailyEligibilityConditions?: string[];
    dailyTimezone?: string | null;
    dailyCutoff?: string | null;
    fees: CommercialFeeRule[];
  };
  assignment: {
    targetType: PayoutAssignmentTarget;
    riderId?: number;
    cohortId?: string;
    partnerId?: number;
    priority?: number;
    effectiveFrom: string;
    effectiveTo?: string;
    supersedesAssignmentId?: string;
  };
}

export interface PayoutAgreementSummary {
  id: string;
  name: string;
  revisions: Array<{
    id: string;
    version: number;
    recipientMode: PayoutRecipientMode;
    earningMethod?: PayoutDeliveryEarningMethod;
    fixedDeliveryAmount?: string | null;
    hybridRule?: 'DAILY_PLUS_DELIVERY' | null;
    hybridDeliveryBasis?: 'STANDARD' | 'FIXED_PER_DELIVERY' | null;
    hybridThreshold?: number | null;
    hybridCountScope?: 'SERVICE_DAY' | null;
    hybridEligibilityBasis?: 'COMPLETED_DELIVERIES_ON_APPROVED_DAY' | null;
    dailyAmount?: string | null;
    dailyEligibilityConditions?: string[];
    dailyTimezone?: string | null;
    dailyCutoff?: string | null;
    feeRules: CommercialFeeRule[];
    publishedAt: string;
    assignments: Array<{
      id: string;
      targetType: PayoutAssignmentTarget;
      riderId?: number | null;
      cohortId?: string | null;
      partnerId?: number | null;
      priority?: number | null;
      effectiveFrom: string;
      effectiveTo?: string | null;
    }>;
  }>;
}

export interface PayoutCohortMembership {
  rider?: { user: { firstName: string; lastName: string } };
  id: string;
  cohortId: string;
  riderId: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  createdByUserId?: number;
  createdAt?: string;
}

export interface PayoutCohortSummary {
  id: string;
  name: string;
  memberships?: PayoutCohortMembership[];
}

export interface PayoutPreviewInput {
  riderId: number;
  from: string;
  to: string;
  bonuses: { reference: string; amount: string }[];
}

export interface PayoutPreview {
  deliveryEarnings: string;
  dailyEarnings?: string;
  tips: string;
  bonuses: string;
  bonusSources: Array<{ reference: string; amount: string }>;
  grossAmount: string;
  commercialFees: Array<
    CommercialFeeRule & { amount: string; calculationBase?: string }
  >;
  netAmount: string | null;
  recipient: {
    mode: PayoutRecipientMode;
    riderId?: number;
    partnerId?: number;
  } | null;
  blockingExceptions: Array<{
    code: string;
    message: string;
    sourceId?: number;
  }>;
  components: Array<{ type: string; label: string; amount: string }>;
  sources: Array<
    PayoutHybridSourceFields & {
      sourceId: number | string;
      agreementRevisionId: string;
      type?: string;
      serviceDate?: string;
      dailyEarnings?: string;
      deliveryEarnings?: string;
    }
  >;
}

export const payoutRoles: AdminRole[] = ['OWNER', 'OPERATIONS', 'FINANCE'];

export interface RecordPayoutServiceDayInput {
  serviceDayId?: string;
  reason?: string;
  riderId: number;
  serviceDate: string;
  revisionId: string;
  evidenceReference: string;
  satisfiedConditions: string[];
  status: 'APPROVED' | 'INELIGIBLE' | 'REVIEW';
}
export interface PayoutServiceDay extends RecordPayoutServiceDayInput {
  id: string;
  actorUserId?: number;
  history?: Array<{
    id: string;
    actorUserId: number;
    reason: string;
    createdAt: string;
    evidence?: Record<string, unknown>;
  }>;
  dailyTimezone?: string;
  dailyCutoff?: string;
}

export interface PayoutRiderOwnership {
  id: string;
  riderId: number;
  ownershipType: 'INDEPENDENT' | 'PARTNER';
  partnerId: number | null;
  partner?: { id: number; name: string; businessName?: string | null } | null;
  effectiveFrom: string;
  effectiveTo: string | null;
}
